// Settings → Developer → Diagnostics: the three pages (brain log, app log,
// snapshot), a Copy on every one of them plus one that copies the lot (docs/10
// §5 rung 5), the auto-send toggle (§4), the dev-unlock disclosure (§4
// obligation 1) and Preview report (§4 obligation 2).
//
// Rung 5 is the floor of the delivery ladder and the only one that always
// works. The 2026-07-26 incident was diagnosed from photographs of a log
// screen, so the copy affordances are not a convenience — they are the channel
// of last resort. A browser clipboard can be denied or simply absent outside a
// secure context, so a failed copy falls back to the text itself, on screen and
// selected: photographing it is the same channel one rung lower.
//
// Nothing rendered here is ever logged back: lines go to the user's own
// clipboard, which they choose where to send, and a log is not that.

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useT } from '../../../i18n/index.js';
import { buttonClass, quietButtonClass } from '../chrome.js';
import {
  EVERY_SECTION,
  renderBundle,
  renderLine,
  type BundleInput,
  type BundleLogLine,
  type DiagSection,
} from './bundle.js';
import {
  webDiagnostics,
  type DiagnosticsGateway,
  type DiagnosticsPreview,
  type DiagnosticsState,
} from './gateway.js';
import { withDeadline } from './deadline.js';

/** The pages a copy can be scoped to. `BUILD` rides on every one of them. */
const PAGES = ['BRAIN_LOG', 'APP_LOG', 'SNAPSHOT'] as const satisfies readonly DiagSection[];
type Page = (typeof PAGES)[number];

type CopyResult = { ok: true } | { ok: false; text: string };
/**
 * What the preview panel is showing. `empty` and `failed` are deliberately
 * distinct: "there is genuinely nothing to report" and "the report could not be
 * built" look identical to a user shown one message for both, and a §4 promise
 * the user reads as a broken button is not a promise they can check.
 */
type PreviewPhase = 'idle' | 'loading' | 'ready' | 'empty' | 'failed';

function LogLines({ lines, empty }: { lines: readonly BundleLogLine[]; empty: string }): ReactNode {
  if (lines.length === 0) return <p className="text-xs text-muted">{empty}</p>;
  return (
    // Newest last, matching the ring's own append order. Rendered through the
    // same `renderLine` the copy uses, so what is photographed and what is
    // pasted are the same string by construction.
    <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words font-mono text-[11px] leading-tight text-muted">
      {lines.map(renderLine).join('\n')}
    </pre>
  );
}

export function DiagnosticsSection({
  diagnostics = webDiagnostics,
}: {
  /** Test seam; defaults to the brain's own diagnostics centre. */
  diagnostics?: DiagnosticsGateway;
} = {}): ReactNode {
  const t = useT();
  const [page, setPage] = useState<Page>('BRAIN_LOG');
  const [state, setState] = useState<DiagnosticsState | null>(null);
  const [disclosure, setDisclosure] = useState(false);
  const [copied, setCopied] = useState<CopyResult | null>(null);
  const [preview, setPreview] = useState<DiagnosticsPreview | null>(null);
  const [phase, setPhase] = useState<PreviewPhase>('idle');
  const [sent, setSent] = useState(false);
  const [sendFailed, setSendFailed] = useState(false);
  const [sending, setSending] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const fallback = useRef<HTMLTextAreaElement | null>(null);
  const disclosureCard = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let live = true;
    // This section renders only while dev mode is unlocked, so mounting it
    // re-asserts the flag the consent record hangs off. A re-assertion arms
    // nothing; only a genuinely fresh unlock still owing a disclosure does.
    //
    // Under a deadline: storage that never answers must not leave the toggle
    // disabled with nothing said (see `deadline.ts`).
    void withDeadline(diagnostics.open(true)).then((settled) => {
      if (!live) return;
      if (!settled.ok) {
        // Storage refused or never answered (a locked-down browser, private
        // mode with IndexedDB off, a blocked upgrade in another tab). The
        // consent controls cannot be honest about a record that could not be
        // read, so they say so rather than showing a default — the logs and the
        // copy buttons below still work, and they are the rung that matters
        // most.
        setUnavailable(true);
        return;
      }
      setState(settled.value);
      setDisclosure(settled.value.disclosurePending);
    });
    return () => {
      live = false;
    };
  }, [diagnostics]);

  const snapshot = useMemo(() => diagnostics.snapshotJson(state), [diagnostics, state]);

  const bundle = useCallback(
    (): BundleInput => ({
      capturedAtMs: Date.now(),
      build: diagnostics.buildInfo(),
      brainLog: diagnostics.brainLog(),
      appLog: diagnostics.appLog(),
      snapshotJson: snapshot,
      reportBytes: preview?.bytes ?? null,
    }),
    [diagnostics, snapshot, preview],
  );

  async function copy(text: string): Promise<void> {
    setCopied(null);
    try {
      const clipboard = (navigator as Navigator & { clipboard?: Clipboard }).clipboard;
      if (typeof clipboard?.writeText !== 'function') throw new Error('no clipboard');
      await clipboard.writeText(text);
      setCopied({ ok: true });
    } catch {
      // Denied, or no clipboard at all (every browser withholds it outside a
      // secure context). Say so, and hand the text back so the user can select
      // it — or photograph it, which is how this bug was found in the first
      // place.
      setCopied({ ok: false, text });
    }
  }

  useEffect(() => {
    if (copied?.ok === false) fallback.current?.select();
  }, [copied]);

  async function toggleAutoSend(on: boolean): Promise<void> {
    const settled = await withDeadline(diagnostics.setAutoSend(on));
    // A consent write that did not land must not be shown as if it had: the
    // checkbox falls back to the state it was rendered from and the panel says
    // storage is not answering.
    if (!settled.ok) {
      setUnavailable(true);
      return;
    }
    setState(settled.value);
    setSent(false);
  }

  const acknowledge = useCallback(async (): Promise<void> => {
    setDisclosure(false);
    const settled = await withDeadline(diagnostics.markDisclosed());
    // A write that never landed leaves the disclosure owed, so the next unlock
    // shows it again — the safe direction for a §4 obligation.
    if (settled.ok) setState(settled.value);
  }, [diagnostics]);

  useEffect(() => {
    if (!disclosure) return;
    // `aria-modal` is a claim about behaviour, so behave: focus starts inside
    // the card (the house pattern, `SettingsDialog`), and Escape is answered
    // HERE. The settings dialog binds Escape on this same window; without the
    // capture-phase intercept below it would navigate the whole dialog away
    // with the disclosure unread underneath.
    disclosureCard.current?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      // Dismissing IS the acknowledgement: §4 obliges the app to SAY it on the
      // unlock, and it did. Re-showing a statement the user closed would be
      // nagging, not consent.
      void acknowledge();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [disclosure, acknowledge]);

  async function showReport(): Promise<void> {
    setPhase('loading');
    setSent(false);
    setSendFailed(false);
    const settled = await withDeadline(diagnostics.preview());
    if (!settled.ok) {
      // Could not be built — storage refused or never answered. NOT the same
      // thing as having nothing to say, and it must not read as such.
      setPreview(null);
      setPhase('failed');
      return;
    }
    setPreview(settled.value);
    setPhase(settled.value === null ? 'empty' : 'ready');
  }

  async function send(): Promise<void> {
    if (sending) return;
    setSending(true);
    setSendFailed(false);
    // Guarded and awaited: a second tap while the first is in flight would
    // consent and flush the same report twice, and an upload that never
    // settles would leave the button armed forever.
    const settled = await withDeadline(diagnostics.sendNow());
    setSending(false);
    const ok = settled.ok && settled.value;
    setSent(ok);
    setSendFailed(!ok);
  }

  const offersUpload = state?.offersUpload ?? false;
  const brainLines = diagnostics.brainLog();
  const appLines = diagnostics.appLog();

  return (
    <>
      {disclosure && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-6">
          <div
            ref={disclosureCard}
            role="dialog"
            aria-modal="true"
            tabIndex={-1}
            aria-labelledby="diag-disclosure-title"
            className="flex w-full max-w-sm flex-col gap-3 rounded-2xl bg-surface p-5 outline-none"
          >
            <h2 id="diag-disclosure-title" className="text-base font-semibold text-ink">
              {t('diag.disclosureTitle')}
            </h2>
            <p className="text-sm text-muted">{t('diag.disclosureBody')}</p>
            <p className="text-sm text-muted">{t('diag.disclosureNever')}</p>
            <p className="text-sm text-muted">
              {t('diag.disclosureWhere', { toggle: t('diag.autoSend') })}
            </p>
            <button type="button" className={buttonClass} onClick={() => void acknowledge()}>
              {t('diag.disclosureOk')}
            </button>
          </div>
        </div>
      )}

      <section
        aria-label={t('diag.title')}
        className="flex flex-col gap-3 rounded-xl bg-surface p-4"
      >
        <h2 className="text-sm font-semibold text-ink">{t('diag.title')}</h2>

        <div role="tablist" aria-label={t('diag.title')} className="flex flex-wrap gap-2">
          {PAGES.map((candidate) => (
            <button
              key={candidate}
              type="button"
              role="tab"
              aria-selected={candidate === page}
              className={candidate === page ? buttonClass : quietButtonClass}
              onClick={() => {
                setPage(candidate);
                setCopied(null);
              }}
            >
              {t(
                candidate === 'BRAIN_LOG'
                  ? 'diag.tabBrainLog'
                  : candidate === 'APP_LOG'
                    ? 'diag.tabAppLog'
                    : 'diag.tabSnapshot',
              )}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className={quietButtonClass}
            onClick={() => void copy(renderBundle(bundle(), [page]))}
          >
            {t('diag.copyPage')}
          </button>
          <button
            type="button"
            className={buttonClass}
            onClick={() => void copy(renderBundle(bundle(), EVERY_SECTION))}
          >
            {t('diag.copyAll')}
          </button>
        </div>

        {copied?.ok === true && (
          <p role="status" className="text-xs text-muted">
            {t('diag.copied')}
          </p>
        )}
        {copied?.ok === false && (
          <>
            <p role="alert" className="text-xs text-danger">
              {t('diag.copyFailed')}
            </p>
            <textarea
              ref={fallback}
              readOnly
              rows={8}
              aria-label={t('diag.copyFallbackLabel')}
              value={copied.text}
              className="w-full rounded-xl border border-edge bg-raised p-2 font-mono text-[11px] text-ink"
            />
          </>
        )}

        {page === 'BRAIN_LOG' && <LogLines lines={brainLines} empty={t('diag.logEmpty')} />}
        {page === 'APP_LOG' && <LogLines lines={appLines} empty={t('diag.logEmpty')} />}
        {page === 'SNAPSHOT' && (
          <pre
            data-testid="diag-snapshot"
            className="max-h-64 overflow-auto whitespace-pre-wrap break-words font-mono text-[11px] leading-tight text-muted"
          >
            {snapshot}
          </pre>
        )}
      </section>

      <section
        aria-label={t('diag.autoSend')}
        className="flex flex-col gap-3 rounded-xl bg-surface p-4"
      >
        <label className="flex min-h-touch items-center gap-3 text-sm text-ink">
          <input
            type="checkbox"
            role="switch"
            checked={state?.autoSend ?? false}
            disabled={state === null}
            onChange={(e) => void toggleAutoSend(e.target.checked)}
            className="h-5 w-5"
          />
          {t('diag.autoSend')}
        </label>
        <p className="text-xs text-muted">{t('diag.autoSendHelp')}</p>
        {state !== null && !offersUpload && (
          <p className="text-xs text-muted">{t('diag.autoSendOff')}</p>
        )}
        {unavailable && (
          <p role="alert" className="text-xs text-danger">
            {t('diag.unavailable')}
          </p>
        )}
      </section>

      <section
        aria-label={t('diag.previewTitle')}
        className="flex flex-col gap-3 rounded-xl bg-surface p-4"
      >
        <h2 className="text-sm font-semibold text-ink">{t('diag.previewTitle')}</h2>
        <p className="text-xs text-muted">{t('diag.previewHelp')}</p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className={quietButtonClass}
            disabled={phase === 'loading'}
            onClick={() => void showReport()}
          >
            {t('diag.previewAction')}
          </button>
        </div>
        {phase === 'loading' && (
          <p role="status" className="text-xs text-muted">
            {t('diag.previewLoading')}
          </p>
        )}
        {phase === 'empty' && (
          // Genuinely nothing to say, said in a way that cannot be mistaken for
          // a dead button: what "empty" means here, and the two live counters
          // that prove the rings are recording.
          <div
            data-testid="diag-preview-empty"
            className="flex flex-col gap-1 rounded-xl border border-edge p-3"
          >
            <p role="status" className="text-xs text-ink">
              {t('diag.previewEmpty')}
            </p>
            <p className="text-xs text-muted">
              {t('diag.previewEmptyWhy', { brain: brainLines.length, app: appLines.length })}
            </p>
          </div>
        )}
        {phase === 'failed' && (
          <p role="alert" className="text-xs text-danger">
            {t('diag.previewFailed')}
          </p>
        )}
        {phase === 'ready' && preview !== null && (
          <>
            {/* The EXACT bytes, as the brain rendered them. Never reformatted: a
                promise of anonymisation the user cannot inspect is not a
                promise, and neither is one they inspect through a filter. */}
            <pre
              data-testid="diag-preview"
              className="max-h-64 overflow-auto whitespace-pre-wrap break-words font-mono text-[11px] leading-tight text-muted"
            >
              {preview.bytes}
            </pre>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className={quietButtonClass}
                onClick={() => void copy(preview.bytes)}
              >
                {t('diag.previewCopy')}
              </button>
              {/* Offered only while the toggle allows an upload at all: a
                  "Send" the app has promised not to honour is worse than no
                  button (docs/10 §4). */}
              {offersUpload && (
                <button
                  type="button"
                  className={buttonClass}
                  disabled={sending}
                  onClick={() => void send()}
                >
                  {t('diag.previewSend')}
                </button>
              )}
            </div>
            {sent && (
              <p role="status" className="text-xs text-muted">
                {t('diag.previewSent')}
              </p>
            )}
            {sendFailed && (
              <p role="alert" className="text-xs text-danger">
                {t('diag.previewSendFailed')}
              </p>
            )}
          </>
        )}
      </section>
    </>
  );
}
